/**
 * Preferences Service for WERESP_WEB
 * Handles user preferences storage and retrieval from the backend
 */

import $api from '../http'
import i18n from '../i18n/config'

type ThemeType =
  | 'light'
  | 'dark'
  | 'dark-monochrome'
  | 'darker-monochrome'
  | 'auto'

interface UserPreferences {
  theme?: ThemeType
  language?: string
  timezone?: string
  notifications_enabled?: boolean
  auto_save?: boolean
  default_view?: string
  chart_type?: string
  date_format?: string
  time_format?: string
  page_size?: number
  custom_colors?: Record<string, unknown>
  tour_enabled?: boolean
  [key: string]: unknown
}

type PreferenceListener = (preferences: UserPreferences) => void

class PreferencesService {
  private preferences: UserPreferences | null = null
  private listeners: PreferenceListener[] = []

  constructor() {
    // Note: baseURL not needed as we use $api which handles the base URL
  }

  /**
   * Initialize preferences service
   */
  async init(): Promise<void> {
    try {
      await this.loadPreferences()
      this.applyPreferences()
    } catch (error) {
      console.error('Failed to initialize preferences:', error)
      this.setDefaultPreferences()
    }
  }

  /**
   * Load user preferences from backend
   */
  async loadPreferences(): Promise<UserPreferences | null> {
    try {
      const response = await $api.get('/preferences')

      if (response.data.success) {
        // Check if user is authenticated
        if (response.data.authenticated && response.data.preferences) {
          // Check if we have a stored frontend theme that differs from backend
          const storedTheme = localStorage.getItem('currentTheme')
          const backendTheme = response.data.preferences.theme

          // If we have a stored theme and it's different from backend, use the stored one
          if (storedTheme && storedTheme !== backendTheme) {
            this.preferences = {
              ...response.data.preferences,
              theme: storedTheme as ThemeType,
            }
          } else {
            this.preferences = response.data.preferences
          }

          this.saveToLocalStorage()
          return this.preferences
        } else {
          // User not authenticated or no preferences found
          console.log(
            'PreferencesService: User not authenticated or no preferences found - using local preferences'
          )
          return null
        }
      }

      // If no preferences found, return null (will use defaults)
      return null
    } catch (error) {
      // Handle any unexpected errors
      console.error('Error loading preferences:', error)
      return null
    }
  }

  /**
   * Save preferences to backend
   */
  async savePreferences(preferences: UserPreferences): Promise<boolean> {
    try {
      // Map frontend theme values to backend-supported values
      const backendPreferences = { ...preferences }
      if (backendPreferences.theme === 'dark-monochrome') {
        backendPreferences.theme = 'dark'
      }

      const response = await $api.post('/preferences', backendPreferences)

      if (response.data.success) {
        // Store the full theme value in preferences, not the backend-mapped value
        this.preferences = {
          ...response.data.preferences,
          theme: preferences.theme,
        }
        this.saveToLocalStorage()
        this.applyPreferences()
        this.notifyListeners()
        return true
      }

      throw new Error('Failed to save preferences')
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 401) {
        console.log(
          'PreferencesService: User not authenticated - saving preferences locally only'
        )
        // Save preferences locally when authentication fails
        this.preferences = preferences
        this.saveToLocalStorage()
        this.applyPreferences()
        this.notifyListeners()
        return true
      }
      console.error('Error saving preferences:', error)
      throw error
    }
  }

  /**
   * Update specific preference
   */
  async updatePreference(key: string, value: unknown): Promise<boolean> {
    const updatedPreferences = { ...this.preferences, [key]: value }

    // If updating theme, use the dedicated theme update method
    if (key === 'theme') {
      return await this.updateTheme(value as ThemeType)
    }

    return await this.savePreferences(updatedPreferences)
  }

  /**
   * Update theme preference
   */
  async updateTheme(theme: ThemeType): Promise<boolean> {
    try {
      const response = await $api.post('/preferences/theme', { theme: theme })

      if (response.data.success) {
        // Store the full theme value in preferences, not the backend-mapped value
        this.preferences = { ...response.data.preferences, theme: theme }
        this.saveToLocalStorage()
        this.applyTheme()
        this.notifyListeners()
        return true
      }

      throw new Error('Failed to update theme')
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status?: number }
        message?: string
      }
      if (axiosError.response?.status === 401) {
        console.log(
          'PreferencesService: User not authenticated - applying theme locally only'
        )
        // Don't throw error, just apply theme locally
        if (!this.preferences) {
          this.preferences = {}
        }
        this.preferences.theme = theme
        this.saveToLocalStorage()
        this.applyTheme()
        this.notifyListeners()
        return true
      }

      console.error('Error updating theme:', error)

      // Fallback: Apply theme locally even if backend fails
      if (!this.preferences) {
        this.preferences = {}
      }
      this.preferences.theme = theme
      this.saveToLocalStorage()
      this.applyTheme()
      this.notifyListeners()

      // Don't throw error since theme was applied locally
      console.warn(
        'Theme applied locally due to backend failure:',
        axiosError.message
      )
      return true
    }
  }

  /**
   * Reset preferences to defaults
   */
  async resetPreferences(): Promise<boolean> {
    try {
      const response = await $api.post('/preferences/reset')

      if (response.data.success) {
        this.preferences = response.data.preferences
        this.saveToLocalStorage()
        this.applyPreferences()
        this.notifyListeners()
        return true
      }

      throw new Error('Failed to reset preferences')
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 401) {
        console.warn(
          'PreferencesService: Authentication failed (401) - cannot reset preferences'
        )
        throw new Error('Authentication required to reset preferences')
      }
      console.error('Error resetting preferences:', error)
      throw error
    }
  }

  /**
   * Get current preferences
   */
  getPreferences(): UserPreferences | null {
    return this.preferences
  }

  /**
   * Get specific preference value
   */
  getPreference(key: string): unknown {
    return this.preferences ? this.preferences[key] : null
  }

  /**
   * Check if user is authenticated (by testing preferences endpoint)
   */
  async checkAuthentication(): Promise<boolean> {
    try {
      const response = await $api.get('/preferences')
      return response.data.authenticated || false
    } catch (error) {
      console.error('Error checking authentication:', error)
      return false
    }
  }

  /**
   * Apply preferences to the UI
   */
  applyPreferences(): void {
    if (!this.preferences) return

    // Apply theme
    this.applyTheme()

    // Apply other preferences
    this.applyLanguagePreference()
    this.applyTimezonePreference()
  }

  /**
   * Apply theme preference
   */
  applyTheme(): void {
    if (!this.preferences) return

    const theme = this.preferences.theme || 'darker-monochrome'
    const html = document.documentElement

    // Remove existing theme classes
    html.classList.remove(
      'light',
      'dark',
      'dark-monochrome',
      'darker-monochrome',
      'theme-light',
      'theme-dark',
      'theme-dark-monochrome',
      'theme-darker-monochrome'
    )

    // Determine the actual theme to apply (handle 'auto')
    let actualTheme: 'light' | 'dark' | 'dark-monochrome' | 'darker-monochrome'
    if (theme === 'auto') {
      // Auto theme based on system preference
      const prefersDark = window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches
      actualTheme = prefersDark ? 'dark' : 'light'
      html.classList.add(actualTheme)
      html.classList.add(`theme-${actualTheme}`)
    } else {
      actualTheme = theme as
        | 'light'
        | 'dark'
        | 'dark-monochrome'
        | 'darker-monochrome'
      html.classList.add(actualTheme)
      html.classList.add(`theme-${actualTheme}`)
    }

    // Add generic 'dark' class for TailwindCSS dark mode support
    // This enables all dark: variants in Tailwind components
    if (actualTheme !== 'light') {
      html.classList.add('dark')
    }

    // Update theme meta tag
    const themeMeta = document.querySelector('meta[name="theme-color"]')
    if (themeMeta) {
      const themeColor =
        actualTheme === 'dark'
          ? '#1f2937'
          : actualTheme === 'dark-monochrome'
            ? '#2a2a2a'
            : actualTheme === 'darker-monochrome'
              ? '#0a0a0a'
              : '#ffffff'
      themeMeta.setAttribute('content', themeColor)
    }

    // Update VTK.js background colors (use actualTheme, not theme which might be 'auto')
    this.updateVTKBackgroundColors(actualTheme)
  }

  /**
   * Update VTK.js background colors for all active contexts
   */
  updateVTKBackgroundColors(
    theme: 'light' | 'dark' | 'dark-monochrome' | 'darker-monochrome'
  ): void {
    // Use the VTK context manager to update all registered contexts
    if ((window as any).vtkContextManager) {
      ;(window as any).vtkContextManager.updateTheme(theme)
    }
  }

  /**
   * Apply language preference
   */
  applyLanguagePreference(): void {
    if (!this.preferences) return

    const language = this.preferences.language || 'en'

    // Update document language
    document.documentElement.lang = language

    // Update i18n language
    if (i18n.language !== language) {
      i18n.changeLanguage(language).catch((error) => {
        console.error('Failed to change i18n language:', error)
      })
    }

    // Update any language-dependent elements
    const languageElements = document.querySelectorAll('[data-language]')
    languageElements.forEach((element) => {
      element.setAttribute('data-language', language)
    })
  }

  /**
   * Apply timezone preference (for display/formatting)
   * Timezone is stored in preferences and used when formatting dates/times
   */
  applyTimezonePreference(): void {
    if (!this.preferences) return

    const timezone = this.preferences.timezone

    if (timezone) {
      try {
        // Validate timezone by attempting to use it
        new Date().toLocaleString(undefined, { timeZone: timezone })
        document.documentElement.dataset.timezone = timezone
      } catch {
        // Invalid timezone, use default
        document.documentElement.dataset.timezone =
          Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    } else {
      document.documentElement.dataset.timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  }

  /**
   * Set default preferences
   */
  setDefaultPreferences(): void {
    this.preferences = {
      theme: 'dark',
      language: 'en',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      notifications_enabled: true,
      auto_save: true,
      default_view: 'dashboard',
      chart_type: 'line',
      date_format: 'YYYY-MM-DD',
      time_format: '24h',
      page_size: 10,
      custom_colors: {},
    }
    this.saveToLocalStorage()
    this.applyPreferences()
  }

  /**
   * Save preferences to localStorage as backup
   */
  saveToLocalStorage(): void {
    if (this.preferences) {
      localStorage.setItem('user_preferences', JSON.stringify(this.preferences))
    }
  }

  /**
   * Load preferences from localStorage
   */
  loadFromLocalStorage(): boolean {
    const stored = localStorage.getItem('user_preferences')
    if (stored) {
      try {
        this.preferences = JSON.parse(stored)
        return true
      } catch (error) {
        console.error('Error parsing stored preferences:', error)
      }
    }
    return false
  }

  /**
   * Setup theme change listener
   */
  setupThemeListener(): void {
    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', () => {
      if (this.preferences && this.preferences.theme === 'auto') {
        this.applyTheme()
      }
    })
  }

  /**
   * Add preference change listener
   */
  addListener(callback: PreferenceListener): void {
    this.listeners.push(callback)
  }

  /**
   * Remove preference change listener
   */
  removeListener(callback: PreferenceListener): void {
    const index = this.listeners.indexOf(callback)
    if (index > -1) {
      this.listeners.splice(index, 1)
    }
  }

  /**
   * Notify all listeners of preference changes
   */
  private notifyListeners(): void {
    this.listeners.forEach((callback) => {
      try {
        if (this.preferences) {
          callback(this.preferences)
        }
      } catch (error) {
        console.error('Error in preference listener:', error)
      }
    })
  }

  /**
   * Sync with existing localStorage settings
   */
  syncWithExistingSettings(): void {
    // Sync with existing dark mode setting
    const existingDarkMode = localStorage.getItem('isDarkMode')
    if (existingDarkMode !== null && !this.preferences) {
      this.preferences = {
        theme: JSON.parse(existingDarkMode) ? 'dark' : 'light',
        language: 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        notifications_enabled: true,
        auto_save: true,
        default_view: 'dashboard',
        chart_type: 'line',
        date_format: 'YYYY-MM-DD',
        time_format: '24h',
        page_size: 10,
        custom_colors: {},
      }
    }
  }
}

// Create singleton instance
const preferencesService = new PreferencesService()

export default preferencesService
